/**
 * SF-4758: my ranking. Left: the items I rank, best first, as many as I like (drag them, or use
 * the up and down buttons, which also work from the keyboard); the first N count. Right: open
 * items I have not ranked yet.
 * Save sends the ids in order with PUT /me/ballot.
 *
 * SF-5080: one ranking per list. A list that is not open for voting cannot be ranked, so the
 * tab only says so.
 *
 * SF-5087: anyone can rank every open item; only the first N count in the results (sf-api
 * scores only those), so the rows past N are greyed under a line. Unsaved changes also show in
 * a bar, with Save and Undo, that stays at the top of the screen while either column scrolls:
 * it sits above both columns, in a box as tall as the taller one.
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Card, Col, List, Row, Space, Typography } from 'antd';
import { useWarnAboutChange } from '@refinedev/core';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  prioritiesErrorMessage,
  saveBallotArgs,
  useMyBallot,
  usePriorityItems,
  usePrioritiesWrite,
  type ListStatus,
  type PriorityItem,
} from '../api/prioritiesClient';
import { RankedRow } from './RankedRow';
import { LoadError, RefreshWarning } from './LoadError';
import { BELOW_ENVIRONMENT_BAR } from '../../../components/layout/environmentBarSpace';

const sameOrder = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/** How many items were added, removed or moved since the saved order. */
const countChanges = (saved: string[], draft: string[]) =>
  new Set([...saved, ...draft]).size - draft.filter((id, i) => saved[i] === id).length;

export const NOT_VOTING_MESSAGE = 'This list is not open for voting, so rankings cannot be saved.';

interface Props {
  topN: number;
  listId: string;
  /** Missing from an older sf-api, where the only list is always open for voting. */
  listStatus?: ListStatus;
}

export const MyRankingTab: React.FC<Props> = ({ topN, listId, listStatus }) =>
  listStatus && listStatus !== 'voting' ? (
    <Alert type="info" showIcon message={NOT_VOTING_MESSAGE} />
  ) : (
    <Ranking topN={topN} listId={listId} />
  );

const Ranking: React.FC<{ topN: number; listId: string }> = ({ topN, listId }) => {
  const { message } = App.useApp();
  const ballotQuery = useMyBallot(true, listId);
  const itemsQuery = usePriorityItems('open', true, listId);
  const write = usePrioritiesWrite();
  const { setWarnWhen } = useWarnAboutChange();
  const [draft, setDraft] = useState<string[] | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const ballot = ballotQuery.data?.data;
  const openItems = useMemo(() => itemsQuery.data?.data?.items ?? [], [itemsQuery.data]);
  const byId = useMemo(() => new Map(openItems.map((i) => [i.id, i])), [openItems]);
  // The stored order, minus anything closed since. It is never cut to N: lowering N only moves
  // the line, so nothing leaves anyone's ranking.
  const saved = useMemo(() => (ballot?.itemIds ?? []).filter((id) => byId.has(id)), [ballot, byId]);
  const draftOpen = useMemo(() => draft?.filter((id) => byId.has(id)) ?? null, [draft, byId]);
  const ranked = draftOpen ?? saved;
  const dirty = draft !== null && !sameOrder(ranked, saved);
  const changes = dirty ? countChanges(saved, ranked) : 0;
  const unranked = openItems.filter((i) => !ranked.includes(i.id));

  useEffect(() => {
    setWarnWhen(dirty);
    return () => setWarnWhen(false);
  }, [dirty, setWarnWhen]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const change = (next: string[]) => {
    setSaveError(null);
    setDraft(next);
  };
  const move = (from: number, to: number) => change(arrayMove(ranked, from, to));
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    move(ranked.indexOf(String(active.id)), ranked.indexOf(String(over.id)));
  };

  const save = async () => {
    // Edits stay possible while the save is pending. Only drop the draft if it is still the one
    // that was sent; a newer one is kept and, differing from the saved order, stays unsaved.
    const sent = draft;
    // A new attempt replaces the last attempt's error, whatever this one's outcome.
    setSaveError(null);
    try {
      await write.run(saveBallotArgs(ranked, listId), { ballotSaved: true });
      setDraft((current) => (current === sent ? null : current));
      void message.success('Your ranking is saved');
    } catch (error) {
      setSaveError(prioritiesErrorMessage(error, 'Your ranking could not be saved.'));
    }
  };

  // The full error only when there is nothing to show. A failed refetch (every write refreshes
  // every priorities read) keeps the cached lists and the unsaved draft, with a warning above.
  if ((ballotQuery.isError && !ballot) || (itemsQuery.isError && !itemsQuery.data)) {
    return (
      <LoadError
        error={ballotQuery.error ?? itemsQuery.error}
        fallback="Your ranking could not be loaded."
        onRetry={() =>
          Promise.all([
            ballotQuery.isError && ballotQuery.refetch(),
            itemsQuery.isError && itemsQuery.refetch(),
          ])
        }
      />
    );
  }
  const refreshFailed = ballotQuery.isError || itemsQuery.isError;
  const loading = ballotQuery.isLoading || itemsQuery.isLoading;
  const newItems = ballot?.updatedAt ? ballot.newItemsSinceUpdate : 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {refreshFailed && (
        <RefreshWarning
          error={ballotQuery.error ?? itemsQuery.error}
          fallback="Your ranking could not be refreshed."
          onRetry={() =>
            Promise.all([
              ballotQuery.isError && ballotQuery.refetch(),
              itemsQuery.isError && itemsQuery.refetch(),
            ])
          }
        />
      )}
      <Typography.Text strong>Rank as many as you like. Your top {topN} count.</Typography.Text>
      {newItems > 0 && (
        <Alert
          type="info"
          showIcon
          message={`${newItems} new ${newItems === 1 ? 'item' : 'items'} since you last saved`}
        />
      )}
      {saveError && <Alert type="error" showIcon message={saveError} />}
      {/* The bar is sticky inside this box, which holds both columns, so it stays in view while
          a long unranked column scrolls past a short ranking (the ranking card alone would end,
          taking the bar with it). */}
      <div data-testid="ranking-workspace">
        {dirty && (
          <div
            role="status"
            style={{
              position: 'sticky',
              // SF-5172: below the environment bar, which is sticky at the top of the window.
              top: BELOW_ENVIRONMENT_BAR,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: 8,
              marginBottom: 8,
              background: '#fffbe6',
              border: '1px solid #ffe58f',
              borderRadius: 6,
            }}
          >
            <Typography.Text style={{ flex: 1 }}>
              Unsaved: {changes} {changes === 1 ? 'change' : 'changes'}
            </Typography.Text>
            <Button size="small" onClick={() => change(saved)}>
              Undo
            </Button>
            <Button
              size="small"
              type="primary"
              aria-label="Save my ranking"
              onClick={save}
              loading={write.isLoading}
            >
              Save
            </Button>
          </div>
        )}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Card title={`My ranking (${ranked.length} ranked)`} loading={loading} size="small">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext items={ranked} strategy={verticalListSortingStrategy}>
                  <ol aria-label="My ranking" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {ranked.map((id, index) => [
                      index === topN && (
                        <li
                          key="cut"
                          role="separator"
                          style={{
                            padding: '6px 0',
                            borderTop: '2px dashed #bfbfbf',
                            color: 'rgba(0, 0, 0, 0.45)',
                            textAlign: 'center',
                          }}
                        >
                          Below this line does not count
                        </li>
                      ),
                      <RankedRow
                        key={id}
                        item={byId.get(id) as PriorityItem}
                        counts={index < topN}
                        position={index + 1}
                        isFirst={index === 0}
                        isLast={index === ranked.length - 1}
                        onUp={() => move(index, index - 1)}
                        onDown={() => move(index, index + 1)}
                        onRemove={() => change(ranked.filter((r) => r !== id))}
                      />,
                    ])}
                  </ol>
                </SortableContext>
              </DndContext>
              {ranked.length === 0 && (
                <Typography.Text type="secondary">
                  Add items from the list on the right.
                </Typography.Text>
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Not ranked yet" loading={loading} size="small">
              <List
                aria-label="Not ranked yet"
                dataSource={unranked}
                locale={{ emptyText: 'Every open item is in your ranking' }}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        key="add"
                        size="small"
                        aria-label={`Add ${item.title}`}
                        onClick={() => change([...ranked, item.id])}
                      >
                        Add
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta title={item.title} description={item.description} />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      </div>
      <Space>
        <Button type="primary" onClick={save} loading={write.isLoading} disabled={!dirty}>
          Save ranking
        </Button>
        {dirty && <Typography.Text type="warning">You have unsaved changes</Typography.Text>}
      </Space>
    </Space>
  );
};
